import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trophy, Loader2, CheckCircle2, XCircle, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSignupProofs = ({ adminKey }) => {
  const [subs, setSubs] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [note, setNote] = useState({});
  const [busy, setBusy] = useState({});

  const client = useMemo(() => axios.create({ headers: { "X-Admin-Key": adminKey } }), [adminKey]);

  const load = useCallback(() => {
    setLoading(true);
    client.get(`${API}/admin/affiliate-signups`)
      .then((r) => { setSubs(r.data.submissions); setCounts(r.data.counts); })
      .catch((err) => { console.error(err); toast.error("Could not load submissions"); })
      .finally(() => setLoading(false));
  }, [client]);

  useEffect(load, [load]);

  const review = async (id, approve) => {
    setBusy({ ...busy, [id]: true });
    try {
      await client.post(`${API}/admin/affiliate-signups/${id}/review`, { approve, admin_note: note[id] || "" });
      toast.success(approve ? "Approved" : "Rejected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Review failed");
    } finally { setBusy({ ...busy, [id]: false }); }
  };

  const filtered = subs.filter((s) => filter === "all" || s.status === filter);

  return (
    <section data-testid="admin-signup-proofs-section" className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <button data-testid="signup-filter-pending" onClick={() => setFilter("pending")} className={`p-4 rounded-xl border text-left ${filter === "pending" ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
          <p className="text-xs uppercase font-semibold text-amber-700">Pending review</p>
          <p className="text-3xl font-bold text-amber-900 mt-1">{counts.pending}</p>
        </button>
        <button data-testid="signup-filter-approved" onClick={() => setFilter("approved")} className={`p-4 rounded-xl border text-left ${filter === "approved" ? "border-emerald-400 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
          <p className="text-xs uppercase font-semibold text-emerald-700">Approved</p>
          <p className="text-3xl font-bold text-emerald-900 mt-1">{counts.approved}</p>
        </button>
        <button data-testid="signup-filter-rejected" onClick={() => setFilter("rejected")} className={`p-4 rounded-xl border text-left ${filter === "rejected" ? "border-rose-400 bg-rose-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
          <p className="text-xs uppercase font-semibold text-rose-700">Rejected</p>
          <p className="text-3xl font-bold text-rose-900 mt-1">{counts.rejected}</p>
        </button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center text-sm text-slate-400">Nothing to review in this bucket 👍</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <div key={s.id} data-testid={`signup-row-${s.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <p className="font-semibold text-slate-900 inline-flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-emerald-600" /> {s.program_name}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {s.user_name} · <span className="font-mono">{s.user_email}</span> · {new Date(s.submitted_at).toLocaleString()}
                  </p>
                </div>
                <span className={`text-[10px] uppercase font-bold px-2.5 py-1 rounded-full border ${
                  s.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200"
                  : s.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-rose-50 text-rose-700 border-rose-200"
                }`}>{s.status}</span>
              </div>

              {s.proof_url && (
                <a href={s.proof_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 font-semibold mb-2 break-all">
                  <ExternalLink className="w-3 h-3" /> {s.proof_url}
                </a>
              )}
              {s.proof_note && <p className="text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-lg p-3 mb-3">&ldquo;{s.proof_note}&rdquo;</p>}
              {s.admin_note && <p className="text-xs text-slate-500 italic mb-3">Admin note: {s.admin_note}</p>}

              {s.status === "pending" && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    data-testid={`admin-note-${s.id}`}
                    value={note[s.id] || ""}
                    onChange={(e) => setNote({ ...note, [s.id]: e.target.value })}
                    placeholder="Optional admin note (visible to user)"
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid={`reject-${s.id}`}
                      disabled={busy[s.id]}
                      onClick={() => review(s.id, false)}
                      className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm font-semibold hover:border-rose-400 hover:text-rose-600 inline-flex items-center gap-1"
                    >
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                    <button
                      data-testid={`approve-${s.id}`}
                      disabled={busy[s.id]}
                      onClick={() => review(s.id, true)}
                      className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-500 inline-flex items-center gap-1"
                    >
                      {busy[s.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> Approve</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
